import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen, Sun, Moon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import { CollapsedUserAvatar } from "./CollapsedUserAvatar";
import { SidebarNavItems } from "./SidebarNavItems";

import {
  type NavSidebarGroup,
  getActiveNavGroup,
  getNavGroupEntryHref,
  useAuthorizedNavGroups,
  useNavActiveState,
} from "./NavSidebar";
import { useNavSidebarState } from "@/hooks/useNavSidebarState";
import { useTheme } from "@/hooks/useTheme";

export const NAV_SPACE_RAIL_W = 60;
const NAV_SIDEBAR_W_COLLAPSED = 60;
const NAV_SIDEBAR_W_EXPANDED = 230;

export const NAV_TOTAL_W_COLLAPSED = NAV_SPACE_RAIL_W + NAV_SIDEBAR_W_COLLAPSED;

export const NAV_TOTAL_W_EXPANDED = NAV_SPACE_RAIL_W + NAV_SIDEBAR_W_EXPANDED;

/** Header title width when expanded: sidebar minus its 12 px insets and the 36 px toggle + 8 px gap. */
const NAV_TITLE_W = NAV_SIDEBAR_W_EXPANDED - 2 * 12 - 44;

/**
 * Same compact rhythm as NavSidebar.
 *
 * Collapsed width: 60px
 * Horizontal padding: 12px + 12px
 * Item slot: 36px
 */
const NAV_ITEM_SIZE_CLASS = "h-9 w-9";
const NAV_ITEM_RADIUS_CLASS = "rounded-md";
const NAV_ICON_CLASS = "h-[18px] w-[18px]";
const NAV_SECTION_X_PADDING_CLASS = "px-3";
const NAV_VERTICAL_GAP_CLASS = "gap-1";

// Single-page spaces do not need a second navigation rail.
const NO_SECONDARY_SIDEBAR_GROUPS = new Set(["home", "journal", "operations", "investigations"]);

export function shouldShowSecondaryNavSidebar(pathname: string) {
  return !NO_SECONDARY_SIDEBAR_GROUPS.has(getActiveNavGroup(pathname).id);
}

const SPACE_RAIL_INDICATOR_ROW_GAP = 40;
// Header (56) + nav padding (12) + centring in a 36px slot (10).
const SPACE_RAIL_INDICATOR_BASE_TOP = 78;

interface TooltipState {
  label: string;
  midY: number;
  left: number;
}

function SpaceRail({
  activeGroup,
  activeGroupIndicatorTop,
  groups,
  onUserTooltip,
  onUserTooltipHide,
  onTooltip,
  onTooltipHide,
}: {
  activeGroup: NavSidebarGroup;
  activeGroupIndicatorTop: number;
  groups: NavSidebarGroup[];
  onUserTooltip: (label: string, el: HTMLElement) => void;
  onUserTooltipHide: () => void;
  onTooltip: (label: string, el: HTMLElement) => void;
  onTooltipHide: () => void;
}) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 hidden h-full flex-col overflow-hidden md:flex",
        "border-r border-brand-cyan/25 dark:border-white/5 bg-brand-navy dark:bg-surface text-white",
      )}
      style={{ width: NAV_SPACE_RAIL_W }}
      aria-label="Spaces navigation"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-0 z-10 h-4 w-0.5 rounded-r-xs bg-brand-cyan"
        style={{ top: activeGroupIndicatorTop }}
      />

      {/* ── Logo mark ─────────────────────────────────────────────── */}
      <div className="flex h-14 max-h-14 shrink-0 items-center justify-center">
        <Link
          to="/home"
          aria-label="Home"
          onMouseEnter={(event) => onTooltip("Home", event.currentTarget)}
          onMouseLeave={onTooltipHide}
          className={cn(
            "flex items-center justify-center",
            NAV_ITEM_SIZE_CLASS,
            NAV_ITEM_RADIUS_CLASS,
            "transition-colors hover:bg-white/10",
            "focus:outline-none focus:ring-2 focus:ring-white/30",
          )}
        >
          <Logo variant="mark" tone="white" size={34} />
        </Link>
      </div>

      {/* ── Spaces / first-level navigation ───────────────────────── */}
      <nav
        className={cn(
          "flex flex-1 flex-col items-center py-3",
          NAV_SECTION_X_PADDING_CLASS,
          NAV_VERTICAL_GAP_CLASS,
        )}
        aria-label="Spaces"
      >
        {groups.map((group) => {
          const GroupIcon = group.icon;
          const isActive = activeGroup.id === group.id;

          return (
            <Link
              key={group.id}
              to={getNavGroupEntryHref(group)}
              aria-label={group.label}
              aria-current={isActive ? "page" : undefined}
              onMouseEnter={(event) => onTooltip(group.label, event.currentTarget)}
              onMouseLeave={onTooltipHide}
              className={cn(
                "flex w-full items-center justify-center",
                NAV_ITEM_SIZE_CLASS,
                NAV_ITEM_RADIUS_CLASS,
                group.id === "admin" && "mt-2",
                "transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-white/30",
                isActive
                  ? "bg-white/15 text-white"
                  : "text-white/70 hover:bg-white/10 hover:text-white",
              )}
            >
              <GroupIcon className={NAV_ICON_CLASS} />
            </Link>
          );
        })}
      </nav>

      <footer className="mt-auto shrink-0 py-3">
        <div className="flex flex-col items-center gap-1">
          <button
            type="button"
            onClick={toggleTheme}
            onMouseEnter={(event) =>
              onTooltip(isDark ? "Light mode" : "Dark mode", event.currentTarget)
            }
            onMouseLeave={onTooltipHide}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            aria-pressed={isDark}
            className={cn(
              "flex items-center justify-center",
              NAV_ITEM_SIZE_CLASS,
              NAV_ITEM_RADIUS_CLASS,
              "text-white/70 transition-colors hover:bg-white/10 hover:text-white",
            )}
          >
            {isDark ? <Sun className={NAV_ICON_CLASS} /> : <Moon className={NAV_ICON_CLASS} />}
          </button>

          <CollapsedUserAvatar
            onTooltip={onUserTooltip}
            onTooltipHide={onUserTooltipHide}
            variant="ghost"
          />
        </div>
      </footer>
    </aside>
  );
}

export function NavSidebarDouble() {
  const location = useLocation();
  const { mode, toggle } = useNavSidebarState();
  const authorizedGroups = useAuthorizedNavGroups();

  const [tip, setTip] = useState<TooltipState | null>(null);

  const expanded = mode === "expanded";
  const width = expanded ? NAV_SIDEBAR_W_EXPANDED : NAV_SIDEBAR_W_COLLAPSED;

  const activeGroup = getActiveNavGroup(location.pathname, authorizedGroups);
  const activeGroupIndex = authorizedGroups.findIndex((group) => group.id === activeGroup.id);
  const activeGroupIndicatorTop =
    SPACE_RAIL_INDICATOR_BASE_TOP +
    (activeGroupIndex >= 0 ? activeGroupIndex : 0) * SPACE_RAIL_INDICATOR_ROW_GAP;
  const showSecondarySidebar = shouldShowSecondaryNavSidebar(location.pathname);
  const { activeItems, activeItemId } = useNavActiveState(location.pathname, activeGroup);

  const showTip = (label: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();

    setTip({
      label,
      midY: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };

  const toggleSidebar = () => {
    setTip(null);
    toggle();
  };

  return (
    <>
      <SpaceRail
        activeGroup={activeGroup}
        activeGroupIndicatorTop={activeGroupIndicatorTop}
        groups={authorizedGroups}
        onUserTooltip={showTip}
        onUserTooltipHide={() => setTip(null)}
        onTooltip={showTip}
        onTooltipHide={() => setTip(null)}
      />

      {showSecondarySidebar && (
        <aside
          className={cn(
            "fixed top-0 z-20 hidden h-full flex-col overflow-hidden xl:flex",
            "border-r border-border bg-surface",
            "transition-[width] duration-350 ease-[cubic-bezier(0.25,1,0.5,1)] will-change-[width] motion-reduce:transition-none",
          )}
          style={{
            left: NAV_SPACE_RAIL_W,
            width,
          }}
          aria-label={`${activeGroup.label} navigation`}
        >
          {/* ── Sidebar header ──────────────────────────────────────── */}
          <div
            className={cn(
              "flex h-14 max-h-14 shrink-0 items-center overflow-hidden border-b border-border",
              NAV_SECTION_X_PADDING_CLASS,
            )}
          >
            <div className="relative flex w-full min-w-0 items-center">
              {/* Fixed to the expanded width, so the title never reflows while the
                  sidebar collapses: it is clipped and fades instead of wrapping. */}
              <div
                style={{ width: NAV_TITLE_W }}
                className={cn(
                  "pointer-events-none absolute left-0 min-w-0 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
                  expanded
                    ? "translate-x-0 opacity-100 delay-100"
                    : "-translate-x-2 opacity-0 delay-0",
                )}
              >
                {/* A fixed size that fits every current space name on one line; a longer
                    one wraps to a second line rather than being cut or shrunk. */}
                <div
                  className="line-clamp-2 text-nav-title leading-tight font-semibold wrap-break-word text-brand-navy dark:text-primary"
                  title={activeGroup.label}
                >
                  {activeGroup.label}
                </div>
              </div>

              <button
                type="button"
                onClick={toggleSidebar}
                onMouseEnter={
                  !expanded ? (event) => showTip("Expand", event.currentTarget) : undefined
                }
                onMouseLeave={!expanded ? () => setTip(null) : undefined}
                className={cn(
                  "ml-auto flex shrink-0 items-center justify-center",
                  NAV_ITEM_SIZE_CLASS,
                  NAV_ITEM_RADIUS_CLASS,
                  "text-secondary transition-colors hover:bg-surface-elevated hover:text-primary",
                )}
                aria-label={expanded ? "Collapse" : "Expand"}
                aria-expanded={expanded}
              >
                {expanded ? (
                  <PanelLeftClose className={cn(NAV_ICON_CLASS, "opacity-75")} />
                ) : (
                  <PanelLeftOpen className={cn(NAV_ICON_CLASS, "opacity-75")} />
                )}
              </button>
            </div>
          </div>

          {/* ── Nav items for active space ──────────────────────────── */}
          <SidebarNavItems
            items={activeItems}
            activeItemId={activeItemId}
            expanded={expanded}
            className={cn(
              "relative z-10 flex flex-1 flex-col overflow-x-hidden overflow-y-auto py-3",
              NAV_SECTION_X_PADDING_CLASS,
              NAV_VERTICAL_GAP_CLASS,
            )}
            onItemMouseEnter={(label, el) => showTip(label, el)}
            onItemMouseLeave={() => setTip(null)}
          />
        </aside>
      )}

      {/* ── Tooltip ───────────────────────────────────────────────── */}
      {tip && (
        <div
          role="tooltip"
          className={cn(
            "pointer-events-none fixed z-50 whitespace-nowrap rounded-lg",
            "border border-border bg-surface px-2.5 py-1.5",
            "text-sm font-medium text-primary shadow-xl",
          )}
          style={{
            left: tip.left,
            top: tip.midY,
            transform: "translateY(-50%)",
          }}
        >
          {tip.label}
        </div>
      )}
    </>
  );
}
